'use client';

import { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import { Box, Typography } from '@mui/material';
import cx from 'clsx';
import gsap from 'gsap';

import { loadScrollTrigger } from '@/utils/gsap/lazyPlugins';

import styles from './Step.module.scss';

interface StepProps {
  number: string;
  title: string;
  text: string;
  variant: 'pink' | 'green' | 'blue';
}

const Step = ({ number, title, text, variant }: StepProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const targetEl = wrapperRef.current;
      const container = containerRef.current;

      if (!targetEl || !container) return undefined;

      let isCancelled = false;

      const initScrollTrigger = async () => {
        await loadScrollTrigger();

        if (isCancelled || !targetEl || !container) return;

        gsap.to(targetEl, {
          scrollTrigger: {
            trigger: container,
            start: 'top center',
            end: 'bottom center',
            onEnter: () => {
              gsap.to(targetEl, { opacity: 1 });
              gsap.to(container, { '--gradient-opacity': 1 });
            },
            onLeave: () => {
              gsap.to(targetEl, { opacity: 0.1 });
              gsap.to(container, { '--gradient-opacity': 0.1 });
            },
            onEnterBack: () => {
              gsap.to(targetEl, { opacity: 1 });
              gsap.to(container, { '--gradient-opacity': 1 });
            },
            onLeaveBack: () => {
              gsap.to(targetEl, { opacity: 0.1 });
              gsap.to(container, { '--gradient-opacity': 0.1 });
            },
          },
        });
      };

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            initScrollTrigger();
            observer.disconnect();
          }
        },
        { rootMargin: '200px' }
      );

      observer.observe(container);

      return () => {
        isCancelled = true;
        observer.disconnect();
      };
    },
    { scope: containerRef }
  );

  return (
    <Box ref={containerRef} className={cx(styles.container, styles[variant])}>
      <Box ref={wrapperRef} className={styles.wrapper}>
        <Box className={cx(styles.stepWrapper, styles[variant])}>
          <Typography variant="h1" component="p" className={styles.number}>
            {number}
          </Typography>
          <Typography variant="h5" component="h2" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box className={styles.textWrapper}>
          <Typography variant="subtitle1" component="p" className={styles.text}>
            {text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Step;
